import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AiIcon } from "@/assets/Dashboard/ai-icon";
import { Mic, SendHorizonal, PenLine } from "lucide-react";
import { themeAtom } from "@/store/atoms";
import { useAtomValue } from "jotai";
import { MdAttachment } from "react-icons/md";
import { chatbot } from "@/api/endpoints/chatbot";

export default function AIButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<
    { role: "user" | "ai"; text: string; timestamp: Date }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [pendingAiMessage, setPendingAiMessage] = useState<string | null>(null);
  const [typingText, setTypingText] = useState<string>("");
  const [isTyping, setIsTyping] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const theme = useAtomValue(themeAtom);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setOpen(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      month: "short",
      day: "numeric",
    });
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const currentInput = input.trim();
    const userMsg = {
      role: "user" as const,
      text: currentInput,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const response = await chatbot.sendMessage({ message: currentInput });
      const aiText = response.data?.response || response.result?.response || response.message || response.response || "I'm sorry, I couldn't process your request.";
      setPendingAiMessage(aiText);
    } catch (error) {
      setPendingAiMessage("I'm sorry, I encountered an error while processing your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Typing effect for AI message
  useEffect(() => {
    if (!pendingAiMessage) return;
    setTypingText("");
    setIsTyping(true);
    let idx = 0;
    const interval = setInterval(() => {
      idx++;
      setTypingText(pendingAiMessage.slice(0, idx));
      if (idx >= pendingAiMessage.length) {
        clearInterval(interval);
        setMessages((prev) => [
          ...prev,
          { role: "ai", text: pendingAiMessage, timestamp: new Date() },
        ]);
        setPendingAiMessage(null);
        setTypingText("");
        setIsTyping(false);
      }
    }, 65);
    return () => clearInterval(interval);
  }, [pendingAiMessage]);

  // Auto scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, typingText]);

  return (
    <>
      <div className="pointer-events-none fixed bottom-6 right-4 z-[9999999999999999] flex flex-col items-center md:bottom-8 md:right-6">
        <div className="pointer-events-auto mb-3 flex flex-col items-center">
          <div className="relative">
            <div className="max-w-[220px] rounded-2xl border border-white/90 bg-white/15 px-4 py-3 shadow-lg backdrop-blur-md md:max-w-none md:px-5">
              <p className="text-center text-sm font-bold leading-snug tracking-tight text-white md:text-[15px]">
                {t("How Can I Help You?")}
              </p>
            </div>
            
            <div
              className="absolute left-1/2 top-full -mt-px h-0 w-0 -translate-x-1/2 border-x-[9px] border-t-[10px] border-x-transparent border-t-white/90"
              aria-hidden
            />
          </div>

          <div className="relative mt-2">
            <div
              className="pointer-events-none absolute -inset-3 rounded-[22px] bg-gradient-to-br from-[#1CCDE6]/35 via-[#D6E242]/25 to-[#DBD633]/35 blur-lg"
              aria-hidden
            />
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label={t("How Can I Help You?")}
              className="relative cursor-pointer border-none bg-transparent p-0 outline-none ring-0 focus-visible:ring-2 focus-visible:ring-[#1CCDE6] focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              <div className="rounded-[14px] bg-gradient-to-br from-[#1CCDE6] via-[#9ED473] to-[#DBD633] p-[1px] shadow-[0_0_24px_rgba(28,205,230,0.35),0_0_40px_rgba(214,226,66,0.2)]">
                <div
                  className="flex h-[58px] w-[58px] items-center justify-center rounded-[14px]  md:h-16 md:w-16 md:rounded-[15px] bg-[url('assets/support/ailogo.png')] bg-cover bg-center"
                  
                >
                  
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div
          className="fixed inset-0 z-[9999999999999999] flex items-end justify-end"
          onClick={handleBackdropClick}
        >
          <div
            ref={modalRef}
            className="fixed bottom-40 right-0 w-[320px] md:w-[400px] h-[500px] md:h-[600px] m-6 shadow-2xl flex flex-col overflow-hidden bg-white dark:bg-[#1f1f1f] p-4 border-2 dark:border-skyBright/50 border-white/50 md:bottom-44"
          >
            {/* Header */}
            <div className="dark:text-white text-black px-6 py-4 rounded-t-2xl relative">
              <div className="font-bold text-xl flex items-center justify-center gap-3">
                <AiIcon
                  fill={theme === "dark" ? "white" : "black"}
                  className="w-9 h-9"
                />
                AI Trading Mentor
              </div>
            </div>

            {/* Chat Content */}
            <div ref={chatContainerRef} className="flex-1 p-4 overflow-y-auto hide-scrollbar">
              <div className="flex flex-col gap-4">
                {messages.length === 0 && (
                  <div className="text-center">
                    <div className="dark:text-white/70 text-black/70 text-sm mb-3">
                      Welcome! Ask me about trading strategies, risk management, or market analysis.
                    </div>
                    <div className="space-y-2">
                      {["How can I improve my risk management?", "What's the best time to trade?", "How do I analyze market trends?"].map((question, idx) => (
                        <button
                          key={idx}
                          onClick={async () => {
                            if (loading) return;
                            
                            const userMsg = { role: "user" as const, text: question, timestamp: new Date() };
                            setMessages([userMsg]);
                            setLoading(true);
                            
                            try {
                              const response = await chatbot.sendMessage({ message: question });
                              const aiText = response.data?.response || response.result?.response || response.message || response.response || "I'm sorry, I couldn't process your request.";
                              setPendingAiMessage(aiText);
                            } catch (error) {
                              setPendingAiMessage("I'm sorry, I encountered an error while processing your request. Please try again.");
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className="block w-full text-left p-2 text-sm rounded-lg dark:bg-white/10 bg-black/5 dark:text-white text-black hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
                        >
                          "{question}"
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {messages.map((msg, idx) =>
                  msg.role === "user" ? (
                    <div
                      className="flex justify-end items-center md:items-end gap-3"
                      key={`user-${idx}`}
                    >
                      <div className="text-right">
                        <p className="dark:text-white text-black text-xs mb-1">
                          {formatTime(msg.timestamp)}
                        </p>
                        <div className="bg-teal-500 text-black dark:text-white rounded-lg px-4 py-2 max-w-xs">
                          {msg.text}
                        </div>
                      </div>
                      <PenLine className="dark:text-white text-black w-4 h-4" />
                    </div>
                  ) : (
                    <div
                      className="flex justify-start items-start gap-3"
                      key={`ai-${idx}`}
                    >
                      <AiIcon
                        fill={theme === "dark" ? "white" : "black"}
                        className="w-6 h-6 flex-shrink-0"
                      />
                      <div>
                        <p className="dark:text-white text-black text-xs mb-1">
                          {formatTime(msg.timestamp)}
                        </p>
                        <div className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-4 py-2 max-w-xs">
                          {msg.text}
                        </div>
                      </div>
                    </div>
                  )
                )}
                
                {isTyping && typingText && (
                  <div className="flex justify-start items-start gap-3">
                    <AiIcon
                      fill={theme === "dark" ? "white" : "black"}
                      className="w-6 h-6 flex-shrink-0"
                    />
                    <div>
                      <p className="dark:text-white text-black text-xs mb-1">
                        Typing...
                      </p>
                      <div className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-4 py-2 max-w-xs">
                        {typingText}
                        <span className="animate-pulse">|</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Message Input */}
            <div className="dark:bg-white/10 bg-black/10 w-full p-2">
              <form onSubmit={handleSend} className="flex items-center gap-3">
                <button
                  type="button"
                  className="dark:text-white text-black hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <div className="flex items-center gap-2">
                    <MdAttachment className="w-4 h-4 sm:w-5 sm:h-5 -rotate-90" />
                    <Mic className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                </button>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type message"
                  className="flex-1 outline-none bg-transparent dark:text-white text-black placeholder-gray-500 dark:placeholder-gray-400"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={!input.trim() || loading}
                  className="dark:text-white text-black hover:text-teal-600 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <SendHorizonal className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
